const TARGET_EMAIL_DOMAINS = [
  "gmail.com",
  "hotmail.com",
  "outlook.com",
  "yahoo.com.br",
  "icloud.com",
] as const;

const MAX_DOMAIN_DISTANCE = 2;

export type EmailDomainSuggestion = {
  originalEmail: string;
  suggestedEmail: string;
  typedDomain: string;
  suggestedDomain: string;
  distance: number;
};

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  const prev = new Array<number>(b.length + 1);
  const curr = new Array<number>(b.length + 1);

  for (let j = 0; j <= b.length; j += 1) {
    prev[j] = j;
  }

  for (let i = 1; i <= a.length; i += 1) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(
        curr[j - 1] + 1,
        prev[j] + 1,
        prev[j - 1] + cost,
      );
    }
    for (let j = 0; j <= b.length; j += 1) {
      prev[j] = curr[j];
    }
  }

  return prev[b.length];
}

export function detectLikelyEmailDomainTypo(
  email: string,
): EmailDomainSuggestion | null {
  const normalizedEmail = String(email || "").trim().toLowerCase();
  const atIndex = normalizedEmail.lastIndexOf("@");

  if (atIndex <= 0 || atIndex === normalizedEmail.length - 1) return null;

  const localPart = normalizedEmail.slice(0, atIndex);
  const typedDomain = normalizedEmail.slice(atIndex + 1);
  if (!localPart || !typedDomain) return null;
  if (TARGET_EMAIL_DOMAINS.includes(typedDomain as (typeof TARGET_EMAIL_DOMAINS)[number])) {
    return null;
  }

  let bestMatch: { domain: string; distance: number } | null = null;

  for (const domain of TARGET_EMAIL_DOMAINS) {
    const distance = levenshtein(typedDomain, domain);
    if (!bestMatch || distance < bestMatch.distance) {
      bestMatch = { domain, distance };
    }
  }

  if (!bestMatch || bestMatch.distance > MAX_DOMAIN_DISTANCE) {
    return null;
  }

  return {
    originalEmail: normalizedEmail,
    suggestedEmail: `${localPart}@${bestMatch.domain}`,
    typedDomain,
    suggestedDomain: bestMatch.domain,
    distance: bestMatch.distance,
  };
}
